import { consume } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { SchmancyButtonSizeContext, type SchmancyButtonSize } from '@schmancy/primitives/button/context'
import { CONTROL_SIZE } from '@schmancy/theme/control-size'
import { css, html, nothing, svg } from 'lit'
import { customElement, state } from 'lit/decorators.js'

declare global {
	interface Window {
		// eslint-disable-next-line no-underscore-dangle -- public runtime contract: global populated by the external schmancy-icons Vite plugin; renaming breaks consumers
		__siIcons?: Record<string, string>
	}
}

/**
 * Path data comes from `window.__siIcons`, populated by the schmancy-icons Vite
 * plugin. Append `-fill` to a name for the filled variant (`home-fill`).
 */
@customElement('schmancy-icon')
export default class SchmancyIcon extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				place-items: center;
				place-content: center;
				font-size: var(--_schmancy-icon-size, 24px);
				width: 1em;
				height: 1em;
				flex-shrink: 0;
				color: inherit;
			}

			svg {
				width: 100%;
				height: 100%;
				fill: currentColor;
				overflow: visible;
			}
		`,
	]

	@state() private name = ''

	@consume({ context: SchmancyButtonSizeContext, subscribe: true })
	private buttonSize?: SchmancyButtonSize

	private observer?: MutationObserver

	override connectedCallback(): void {
		super.connectedCallback()
		this.name = this.textContent?.trim() ?? ''
		this.observer = new MutationObserver(() => {
			this.name = this.textContent?.trim() ?? ''
		})
		this.observer.observe(this, { childList: true, characterData: true, subtree: true })
		this.setAttribute('aria-hidden', 'true')
		this.setAttribute('translate', 'no')
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		this.observer?.disconnect()
	}

	protected override updated(): void {
		if (this.buttonSize) {
			this.style.setProperty('--_schmancy-icon-size', `${CONTROL_SIZE[this.buttonSize].icon.button}px`)
		} else {
			this.style.removeProperty('--_schmancy-icon-size')
		}
	}

	protected override render(): unknown {
		// eslint-disable-next-line no-underscore-dangle -- public runtime contract: see Window.__siIcons declaration above
		const path = window.__siIcons?.[this.name]
		return html`
			${
				path
					? html`<svg viewBox="0 -960 960 960" xmlns="http://www.w3.org/2000/svg">${svg`<path d="${path}"/>`}</svg>`
					: nothing
			}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-icon': SchmancyIcon
	}
}
